import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth, BetaAccessDeniedError, BetaAccessRevokedError, BETA_GATE_ENABLED } from '../contexts/AuthContext';
import { PasswordStrengthIndicator } from '../components/PasswordStrengthIndicator';
import { validatePassword } from '../utils/passwordValidation';

interface LocationState {
  message?: string;
  email?: string;
}

export function Login(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as LocationState | null;

  const [searchParams] = useSearchParams();
  const { signIn, signUp, betaAccessDenied } = useAuth();
  const [email, setEmail] = useState(locationState?.email || '');
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState<string | null>(locationState?.message || null);
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [isLoading, setIsLoading] = useState(false);

  // Show message if user was signed out due to revoked access (only when beta gate is active)
  useEffect(() => {
    if (BETA_GATE_ENABLED && betaAccessDenied) {
      setAuthError('Your beta access has been revoked or suspended. Please contact support.');
    }
  }, [betaAccessDenied]);

  // Clear location state message after showing
  useEffect(() => {
    if (locationState?.message) {
      window.history.replaceState({}, document.title);
    }
  }, [locationState]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError(null);

    // Validate password on sign up
    if (isSignUp) {
      const validation = validatePassword(password);
      if (!validation.isValid) {
        setAuthError('Please meet all password requirements');
        return;
      }
    }

    setIsLoading(true);

    try {
      if (isSignUp) {
        await signUp(email, password);
      } else {
        await signIn(email, password);
      }
      setEmail('');
      setPassword('');
    } catch (error) {
      if (BETA_GATE_ENABLED && error instanceof BetaAccessDeniedError) {
        // Redirect to request access page with email prefilled
        navigate('/request-access', { state: { email: error.email } });
        return;
      }

      if (BETA_GATE_ENABLED && error instanceof BetaAccessRevokedError) {
        setAuthError(error.message);
        return;
      }

      // Handle Firebase auth errors with user-friendly messages
      if (error instanceof Error) {
        const message = error.message;
        if (message.includes('auth/user-not-found') || message.includes('auth/wrong-password')) {
          setAuthError('Invalid email or password');
        } else if (message.includes('auth/email-already-in-use')) {
          setAuthError('An account with this email already exists. Try signing in instead.');
        } else if (message.includes('auth/weak-password')) {
          setAuthError('Password is too weak. Please use a stronger password.');
        } else if (message.includes('auth/invalid-email')) {
          setAuthError('Please enter a valid email address.');
        } else if (message.includes('auth/too-many-requests')) {
          setAuthError('Too many failed attempts. Please try again later.');
        } else {
          setAuthError(message);
        }
      } else {
        setAuthError('Authentication failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-airy">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-card border border-soft-neutral">
        <div>
          <h1 className="text-4xl text-center font-display text-deep-forest font-light">
            Hesvara
          </h1>
          <h2 className="mt-2 text-center text-muted">
            Caregiver Assistant
          </h2>
        </div>

        {/* Beta badge */}
        <div className="flex justify-center">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-evergreen/10 text-evergreen border border-evergreen/20">
            Private Beta
          </span>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="password" className="label">
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder={isSignUp ? 'Create a strong password' : 'Enter your password'}
              />
              {isSignUp && password && (
                <PasswordStrengthIndicator password={password} className="mt-3" />
              )}
            </div>
          </div>

          {authError && (
            <div className="alert alert-error" role="alert">{authError}</div>
          )}

          <button
            type="submit"
            className="btn-primary w-full flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isLoading ? 'Loading...' : isSignUp ? 'Create Account' : 'Sign In'}
          </button>

          <div className="text-center space-y-2">
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                setAuthError(null);
              }}
              className="text-sm text-slate-ink hover:underline"
            >
              {isSignUp
                ? 'Already have an account? Sign in'
                : "Don't have an account? Sign up"}
            </button>

            {!isSignUp && BETA_GATE_ENABLED && (
              <div>
                <button
                  type="button"
                  onClick={() => navigate('/request-access')}
                  className="text-sm text-evergreen hover:underline"
                >
                  Request beta access
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
